import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ChevronLeft, ChevronRight, Package, Phone, MapPin, 
  Calendar, Clock, CreditCard, Truck, Store, RefreshCw,
  CheckCircle2, XCircle, AlertCircle, User, Download, Share2,
  Pencil, Plus, Minus, Trash2, Copy, Link2
} from 'lucide-react';
import { downloadInvoice, shareInvoiceViaWhatsApp } from '@/utils/invoiceGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCalendar = ({ adminToken }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [subscriptionsData, setSubscriptionsData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [products, setProducts] = useState([]);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editDeliveryCharge, setEditDeliveryCharge] = useState(0);
  const [saving, setSaving] = useState(false);
  const [newPaymentLink, setNewPaymentLink] = useState(null);

  useEffect(() => {
    fetchCalendarData();
    fetchProducts();
  }, [currentDate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?include_out_of_stock=true`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/orders/calendar?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setCalendarData(response.data.orders_by_date || {});
      setSubscriptionsData(response.data.subscriptions_by_date || {});
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const formatDateKey = (day) => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${month}-${dayStr}`;
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setIsEditMode(false);
    setNewPaymentLink(null);
    setShowOrderDialog(true);
  };

  const handleEditClick = () => {
    setEditItems(selectedOrder.items?.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      product: item.product,
      name: item.product?.name || item.name,
      price: item.product?.price || item.price
    })) || []);
    setEditDeliveryCharge(selectedOrder.delivery_charge || 0);
    setIsEditMode(true);
  };

  const handleAddProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const existing = editItems.find(i => i.product_id === productId);
    if (existing) {
      setEditItems(editItems.map(i =>
        i.product_id === productId ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setEditItems([...editItems, {
        product_id: productId,
        product: product,
        quantity: 1,
        name: product.name,
        price: product.price
      }]);
    }
  };

  const handleUpdateQuantity = (productId, delta) => {
    setEditItems(editItems.map(i => {
      if (i.product_id === productId) {
        const newQty = i.quantity + delta;
        return newQty > 0 ? { ...i, quantity: newQty } : i;
      }
      return i;
    }).filter(i => i.quantity > 0));
  };

  const handleRemoveItem = (productId) => {
    setEditItems(editItems.filter(i => i.product_id !== productId));
  };

  const calculateEditSubtotal = () => {
    return editItems.reduce((sum, i) => sum + ((i.price || i.product?.price || 0) * i.quantity), 0);
  };

  const calculateEditTotal = () => {
    return calculateEditSubtotal() + Number(editDeliveryCharge || 0);
  };

  const handleSaveOrder = async () => {
    if (editItems.length === 0) {
      toast.error('Order must have at least one item');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/orders/${selectedOrder.id}/items`,
        {
          items: editItems.map(i => ({
            product_id: i.product_id,
            quantity: i.quantity,
            name: i.name || i.product?.name,
            price: i.price || i.product?.price,
            description: i.product?.description
          })),
          delivery_charge: Number(editDeliveryCharge)
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      if (response.data.new_payment_link) {
        setNewPaymentLink(response.data.new_payment_link);
        toast.success('Order updated! New payment link generated.');
      } else {
        toast.success('Order updated successfully!');
      }
      
      // Update selected order with new data
      setSelectedOrder(response.data.order);
      setIsEditMode(false);
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to update order');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${selectedOrder.id}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setSelectedOrder({ ...selectedOrder, order_status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const config = {
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Paid' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Pending' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' }
    };
    const c = config[status] || config.pending;
    return (
      <Badge className={`${c.color} gap-1`}>
        <c.icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const getOrderStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      confirmed: { color: 'bg-blue-100 text-blue-800', label: 'Confirmed' },
      preparing: { color: 'bg-purple-100 text-purple-800', label: 'Preparing' },
      ready: { color: 'bg-indigo-100 text-indigo-800', label: 'Ready' },
      out_for_delivery: { color: 'bg-orange-100 text-orange-800', label: 'Out for Delivery' },
      completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date().toISOString().split('T')[0];

  // Group products by category
  const productsByCategory = products.reduce((acc, product) => {
    const cat = product.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <Card className="p-4 sm:p-6 lg:col-span-2">
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" size="icon" onClick={prevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-bold text-accent">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <Button variant="outline" size="icon" onClick={nextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }).map((_, index) => (
            <div key={`empty-${index}`} className="aspect-square p-1"></div>
          ))}
          
          {Array.from({ length: daysInMonth }).map((_, index) => {
            const day = index + 1;
            const dateKey = formatDateKey(day);
            const ordersForDay = calendarData[dateKey] || [];
            const subsForDay = subscriptionsData[dateKey] || [];
            const isToday = dateKey === today;
            const isSelected = selectedDate === dateKey;

            return (
              <button
                key={day}
                onClick={() => setSelectedDate(dateKey)}
                className={`aspect-square p-1 rounded-lg border transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/10'
                    : isToday
                    ? 'border-primary/50 bg-amber-50'
                    : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="h-full flex flex-col">
                  <span className={`text-sm ${isToday ? 'font-bold text-primary' : ''}`}>
                    {day}
                  </span>
                  <div className="flex-1 flex items-end justify-center gap-0.5">
                    {ordersForDay.length > 0 && (
                      <Badge className="text-xs bg-primary h-5 min-w-5 flex items-center justify-center px-1">
                        {ordersForDay.length}
                      </Badge>
                    )}
                    {subsForDay.filter(s => !s.is_skipped).length > 0 && (
                      <Badge className="text-xs bg-emerald-600 h-5 min-w-5 flex items-center justify-center px-1">
                        S{subsForDay.filter(s => !s.is_skipped).length > 1 ? subsForDay.filter(s => !s.is_skipped).length : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Selected Date Orders */}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-accent">
            {selectedDate ? `${selectedDate}` : 'Select a date'}
          </h3>
          {selectedDate && (
            <div className="flex gap-2 text-xs">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-primary inline-block"></span>Orders</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-600 inline-block"></span>Subs</span>
            </div>
          )}
        </div>
        
        {selectedDate ? (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {/* Regular Orders */}
            {calendarData[selectedDate]?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Orders ({calendarData[selectedDate].length})</p>
                {calendarData[selectedDate].map((order) => (
                  <Card 
                    key={order.id} 
                    className="p-3 border-l-4 border-l-primary cursor-pointer hover:shadow-md hover:bg-gray-50 transition-all mb-2"
                    onClick={() => handleOrderClick(order)}
                    data-testid={`calendar-order-${order.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">#{order.id.slice(0, 8).toUpperCase()}</span>
                      {getOrderStatusBadge(order.order_status)}
                    </div>
                    <p className="text-sm font-medium">{order.name}</p>
                    <p className="text-xs text-muted-foreground">{order.phone}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm font-bold text-primary">₹{order.total}</p>
                      {getPaymentStatusBadge(order.payment_status)}
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Subscription Deliveries */}
            {subscriptionsData[selectedDate]?.filter(s => !s.is_skipped).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                  Subscription Deliveries ({subscriptionsData[selectedDate].filter(s => !s.is_skipped).length})
                </p>
                {subscriptionsData[selectedDate].filter(s => !s.is_skipped).map((sub) => (
                  <Card key={`${sub.id}-${selectedDate}`} className="p-3 border-l-4 border-l-emerald-500 mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">Subscription</Badge>
                      {sub.is_completed 
                        ? <Badge className="bg-blue-100 text-blue-700 text-xs">Order Generated</Badge>
                        : <Badge className="bg-amber-100 text-amber-700 text-xs">Pending Order</Badge>
                      }
                    </div>
                    <p className="text-sm font-medium">{sub.user_name || sub.user_phone}</p>
                    <p className="text-xs text-muted-foreground">{sub.user_phone}</p>
                    <p className="text-sm mt-1">{sub.product_name} × {sub.quantity}</p>
                    <p className="text-xs text-muted-foreground capitalize">{sub.delivery_type}</p>
                  </Card>
                ))}
              </div>
            )}

            {/* Empty state */}
            {(!calendarData[selectedDate] || calendarData[selectedDate].length === 0) &&
              (!subscriptionsData[selectedDate] || subscriptionsData[selectedDate].filter(s => !s.is_skipped).length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No orders for this date</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Click on a date to view orders</p>
          </div>
        )}
      </Card>

      {/* Order Details/Edit Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={(open) => {
        setShowOrderDialog(open);
        if (!open) {
          setIsEditMode(false);
          setNewPaymentLink(null);
        }
      }}>
        <DialogContent className={`${isEditMode ? 'max-w-4xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Order #{selectedOrder?.id?.slice(0, 8).toUpperCase()}
              {isEditMode && <span className="text-sm font-normal text-muted-foreground">- Editing</span>}
            </DialogTitle>
          </DialogHeader>

          {newPaymentLink ? (
            <div className="py-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
                <h4 className="font-bold text-green-800 mb-2">Order Updated!</h4>
                <p className="text-sm text-green-700 mb-3">New total: ₹{selectedOrder?.total}</p>
                <p className="text-sm text-green-700 mb-3">A new payment link has been generated:</p>
                <div className="flex items-center gap-2">
                  <Input value={newPaymentLink} readOnly className="flex-1 text-sm" />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(newPaymentLink);
                      toast.success('Payment link copied!');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <a href={newPaymentLink} target="_blank" rel="noopener noreferrer">
                    <Button size="icon" variant="outline">
                      <Link2 className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </div>
              <Button className="w-full" onClick={() => {
                setShowOrderDialog(false);
                setNewPaymentLink(null);
              }}>
                Close
              </Button>
            </div>
          ) : isEditMode ? (
            // Edit Mode
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Add Products */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Add Products</Label>
                  <div className="border rounded-lg p-3 max-h-[300px] overflow-y-auto space-y-3">
                    {Object.entries(productsByCategory).map(([category, catProducts]) => (
                      <div key={category}>
                        <p className="text-xs font-medium text-muted-foreground uppercase mb-1">{category}</p>
                        <div className="space-y-1">
                          {catProducts.map((product) => (
                            <div
                              key={product.id}
                              className="flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer text-sm"
                              onClick={() => handleAddProduct(product.id)}
                            >
                              <span className="truncate flex-1">{product.name}</span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="font-medium text-primary">₹{product.price}</span>
                                <Button size="icon" variant="ghost" className="h-6 w-6">
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Current Items */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Order Items</Label>
                  <div className="border rounded-lg p-3 space-y-2">
                    {editItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No items in order</p>
                    ) : (
                      editItems.map((item) => (
                        <div key={item.product_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm truncate flex-1">{item.name || item.product?.name}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-6 w-6"
                              onClick={() => handleUpdateQuantity(item.product_id, -1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-6 w-6"
                              onClick={() => handleUpdateQuantity(item.product_id, 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-red-500"
                              onClick={() => handleRemoveItem(item.product_id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                            <span className="w-14 text-right text-sm font-medium">
                              ₹{(item.price || item.product?.price || 0) * item.quantity}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                    
                    {/* Totals */}
                    <div className="pt-3 border-t space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span>₹{calculateEditSubtotal()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Delivery Charge:</Label>
                        <Input
                          type="number"
                          value={editDeliveryCharge}
                          onChange={(e) => setEditDeliveryCharge(e.target.value)}
                          className="w-24 h-8 text-right"
                          min="0"
                        />
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t">
                        <span>Total:</span>
                        <span className="text-primary">₹{calculateEditTotal()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditMode(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveOrder} disabled={saving} className="gap-2">
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // View Mode
            selectedOrder && (
              <div className="space-y-4">
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={handleEditClick} className="gap-1">
                    <Pencil className="h-3 w-3" />
                    Edit Order
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => {
                      try {
                        downloadInvoice(selectedOrder);
                        toast.success('Invoice downloaded!');
                      } catch (error) {
                        toast.error('Failed to download invoice');
                      }
                    }}
                  >
                    <Download className="h-3 w-3" />
                    Download
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => {
                      try {
                        shareInvoiceViaWhatsApp(selectedOrder);
                        toast.success('Opening WhatsApp...');
                      } catch (error) {
                        toast.error('Failed to share');
                      }
                    }}
                  >
                    <Share2 className="h-3 w-3" />
                    Share
                  </Button>
                </div>

                {/* Status Change */}
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Order Status:</Label>
                  <Select
                    value={selectedOrder.order_status}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="preparing">Preparing</SelectItem>
                      <SelectItem value="ready">Ready</SelectItem>
                      <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  {getPaymentStatusBadge(selectedOrder.payment_status)}
                </div>

                <Separator />

                {/* Customer Details */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <h4 className="font-semibold text-sm text-accent flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Customer Details
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p className="font-medium">{selectedOrder.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <a href={`tel:${selectedOrder.phone}`} className="font-medium text-primary flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {selectedOrder.phone}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Delivery Details */}
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <h4 className="font-semibold text-sm text-accent flex items-center gap-2">
                    {selectedOrder.delivery_type === 'pickup' ? <Store className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
                    {selectedOrder.delivery_type === 'pickup' ? 'Pickup' : 'Delivery'} Details
                  </h4>
                  <div className="text-sm space-y-1">
                    {selectedOrder.delivery_type === 'delivery' && (
                      <p className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <span>{selectedOrder.delivery_address || 'No address provided'}</span>
                      </p>
                    )}
                    {selectedOrder.scheduled_date && (
                      <p className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>Scheduled: <strong>{selectedOrder.scheduled_date}</strong></span>
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>Created: {formatDateTime(selectedOrder.created_at)}</span>
                    </p>
                  </div>
                </div>

                {/* Order Items */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-accent">Order Items</h4>
                  <div className="border rounded-lg divide-y">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="p-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-amber-100 rounded flex items-center justify-center">
                            <Package className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{item.product?.name || 'Product'}</p>
                            <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-medium">₹{(item.product?.price || 0) * item.quantity}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="p-3 bg-amber-50 rounded-lg space-y-2">
                  <h4 className="font-semibold text-sm text-accent flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Payment Summary
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>₹{selectedOrder.subtotal || (selectedOrder.total - (selectedOrder.delivery_charge || 0))}</span>
                    </div>
                    {selectedOrder.delivery_charge > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Delivery Charge</span>
                        <span>₹{selectedOrder.delivery_charge}</span>
                      </div>
                    )}
                    <Separator className="my-2" />
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span className="text-primary">₹{selectedOrder.total}</span>
                    </div>
                  </div>
                  
                  {selectedOrder.payment_link && selectedOrder.payment_status !== 'paid' && (
                    <Button 
                      className="w-full mt-2 bg-primary"
                      onClick={() => window.open(selectedOrder.payment_link, '_blank')}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Open Payment Link
                    </Button>
                  )}
                </div>

                {selectedOrder.notes && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-sm text-blue-800 mb-1">Notes</h4>
                    <p className="text-sm text-blue-700">{selectedOrder.notes}</p>
                  </div>
                )}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCalendar;
